import { ModuleDTO } from 'src/models/dto/ModuleDTO'
import { downloadAsFile } from 'src/services/downloadAsFile'

export const CONTAINER_COLUMN = '__container__'

const PLACEHOLDER_REGEX = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

const SCORE_PLACEHOLDER_KEY = 'scorePlaceHolder'

// A page's labels, which buckets and cups use to find the page.
const PAGE_LABELS_KEY = 'labels'

export interface PlaceholdersByKind {
    content: string[]
    scoring: string[]
    pageLabels: string[]
}

// The field a placeholder sits in decides its kind; any field not listed holds content.
const KIND_BY_FIELD = new Map<string, keyof PlaceholdersByKind>([
    [SCORE_PLACEHOLDER_KEY, 'scoring'],
    [PAGE_LABELS_KEY, 'pageLabels'],
])

export function extractPlaceholders(module: ModuleDTO): PlaceholdersByKind {
    const found: Record<keyof PlaceholdersByKind, Set<string>> = {
        content: new Set(),
        scoring: new Set(),
        pageLabels: new Set(),
    }
    const visit = (value: unknown, key?: string) => {
        if (typeof value === 'string') {
            const into = found[(key && KIND_BY_FIELD.get(key)) || 'content']
            const regex = new RegExp(PLACEHOLDER_REGEX)
            let match: RegExpExecArray | null
            while ((match = regex.exec(value)) !== null) {
                into.add(match[1])
            }
        } else if (Array.isArray(value)) {
            // Strings in an array (e.g. a page's labels) belong to the array's field.
            value.forEach((element) => visit(element, key))
        } else if (value && typeof value === 'object') {
            Object.entries(value).forEach(([childKey, child]) => visit(child, childKey))
        }
    }
    visit(module)
    const names = (kind: keyof PlaceholdersByKind) =>
        Array.from(found[kind]).filter((name) => name !== CONTAINER_COLUMN)
    return { content: names('content'), scoring: names('scoring'), pageLabels: names('pageLabels') }
}

export function getSpreadsheetColumns(module: ModuleDTO): string[] {
    const { content, scoring, pageLabels } = extractPlaceholders(module)
    return [...Array.from(new Set([...content, ...scoring, ...pageLabels])), CONTAINER_COLUMN]
}

export function downloadSampleSpreadsheet(columns: string[], moduleName?: string) {
    downloadAsFile(
        new Blob([`${columns.join(',')}\n`], { type: 'text/csv' }),
        `${moduleName ?? 'import'}_sample.csv`
    )
}
